<!-- Resolves raw chat message file records into attachment metadata and renders them. -->

<script lang="ts" setup>
import type { Json } from '@nzyme/utils/toJson.js';
import { useService } from '@nzyme/vue-ioc/useService.js';
import { computedAsync } from '@vueuse/core';

import { FileMetadataStore } from '@sigma/client/files/FileMetadataStore.js';
import type { File } from '@sigma/files-common/File.js';
import { getFilePreviewUrl } from '@sigma/files-common/getFilePreviewUrl.js';

import ChatMessageAttachments from './ChatMessageAttachments.vue';
import type { ChatMessageAttachmentFile } from './ChatMessageAttachmentsTypes.js';

interface Props {
  files: Json<File>[];
  color: 'blue' | 'white';
}

const props = defineProps<Props>();

const fileMetadataStore = useService(FileMetadataStore);

const resolved = computedAsync<ChatMessageAttachmentFile[]>(
  async () => {
    return await Promise.all(
      props.files.map(async (file): Promise<ChatMessageAttachmentFile> => {
        const meta = await fileMetadataStore.get(file.id);
        return {
          id: file.id.toString(),
          fileName: file.name,
          fileType: file.type,
          url: meta?.url ?? null,
          previewUrl: getFilePreviewUrl(meta?.url, file.type) ?? null,
          width: file.width,
          height: file.height,
          size: file.size,
        };
      }),
    );
  },
  props.files.map(file => ({
    id: file.id.toString(),
    fileName: file.name,
    fileType: file.type,
    width: file.width,
    height: file.height,
    size: file.size,
  })),
);
</script>

<template>
  <ChatMessageAttachments
    :files="resolved"
    :color="color"
  />
</template>
